import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { comics, getComicById, getRelatedComics } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams();
  const comic = getComicById(id || '');
  const related = useMemo(() => (comic ? getRelatedComics(comic) : []), [comic]);
  const [liked, setLiked] = useState(false);

  useEffect(() => {
    if (comic) document.title = `${comic.title} · 短篇日常搞笑 | 韩国歪歪漫画`;
    window.scrollTo(0, 0);
  }, [comic]);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="mx-auto max-w-3xl px-4 py-32 text-center">
          <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
          <h1 className="mt-4 font-heading text-3xl text-foreground-950">没有找到这篇漫画</h1>
          <p className="mt-2 text-sm text-foreground-600">它可能已下线，或链接有误</p>
          <Link to="/comics" className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary-500 px-7 py-3 text-sm font-medium text-background-50 whitespace-nowrap">
            返回漫画合集
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const readingStrips = [
    'https://readdy.ai/api/search-image?query=Funny%20webtoon%20panel%20of%20characters%20interacting%20in%20a%20comic%20panel%2C%20warm%20orange%20and%20cream%20chibi%20style%2C%20speech%20bubbles%2C%20relaxed%20humorous%20scene%2C%20high%20quality%20digital%20comic%20art&width=1400&height=900&seq=strip-1&orientation=landscape',
    'https://readdy.ai/api/search-image?query=Cute%20chibi%20characters%20sharing%20an%20amusing%20everyday%20moment%2C%20playful%20webtoon%20comic%20panel%2C%20warm%20pastel%20orange%20cream%20and%20teal%20palette%2C%20speech%20bubbles%2C%20fun%20casual%20illustration&width=1400&height=900&seq=strip-2&orientation=landscape',
    'https://readdy.ai/api/search-image?query=Whimsical%20chibi%20characters%20having%20a%20hilarious%20reaction%20scene%2C%20funny%20webtoon%20panel%2C%20warm%20orange%20cream%20and%20teal%20colors%2C%20expressive%20comic%20art%2C%20lighthearted%20relaxing%20style&width=1400&height=900&seq=strip-3&orientation=landscape',
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <section className="bg-background-100 pt-24 pb-8 md:pt-32 md:pb-10">
        <div className="mx-auto max-w-6xl px-4 md:px-6">
          <nav className="text-xs text-foreground-600" aria-label="面包屑">
            <ol className="flex flex-wrap items-center gap-1">
              <li><Link to="/" className="hover:text-primary-600">首页</Link></li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li><Link to="/comics" className="hover:text-primary-600">漫画合集</Link></li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li><Link to={`/comics?cat=${comic.category}`} className="hover:text-primary-600">{comic.categoryName}</Link></li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li className="truncate text-foreground-900">{comic.title}</li>
            </ol>
          </nav>

          <div className="mt-8 grid grid-cols-1 gap-8 md:grid-cols-[320px_1fr]">
            <div className="overflow-hidden rounded-2xl border border-background-200 bg-background-50">
              <img src={comic.cover} alt={`${comic.title}封面`} className="w-full object-cover" />
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-primary-100 px-4 py-1.5 text-sm font-medium text-primary-700">{comic.categoryName}</span>
                <span className="rounded-full bg-accent-100 px-4 py-1.5 text-sm font-medium text-accent-900">{comic.episode}</span>
                <span className="rounded-full bg-secondary-100 px-4 py-1.5 text-sm font-medium text-secondary-900">{comic.readingTime}</span>
              </div>
              <h1 className="mt-4 font-heading text-3xl leading-tight text-foreground-950 md:text-5xl">{comic.title}</h1>
              <p className="mt-4 text-base leading-relaxed text-foreground-800">{comic.summary}</p>

              <div className="mt-6 flex flex-wrap gap-2">
                {comic.tags.map((t) => (
                  <span key={t} className="rounded-full bg-background-200 px-3 py-1 text-xs text-foreground-700">#{t}</span>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-4">
                <button
                  type="button"
                  onClick={() => setLiked(!liked)}
                  className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                    liked ? 'bg-primary-500 text-background-50' : 'bg-background-50 text-foreground-800 hover:bg-primary-100'
                  }`}
                >
                  <i className={liked ? 'ri-heart-fill' : 'ri-heart-3-line'}></i>
                  {liked ? '已点赞' : '点赞'} · {comic.likes}
                </button>
                <div className="flex items-center gap-2 text-sm text-foreground-600">
                  <span className="inline-flex items-center gap-1"><i className="ri-eye-line"></i>{comic.views} 次浏览</span>
                  <span className="hidden sm:inline">·</span>
                  <span className="hidden sm:inline">更新于 {comic.updatedAt}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 md:px-6 py-10 md:py-14">
        <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">开始阅读 · {comic.title}</h2>
        <div className="mt-6 space-y-6">
          {readingStrips.map((src, i) => (
            <figure key={i} className="overflow-hidden rounded-2xl border border-background-200 bg-background-100">
              <img src={src} alt={`${comic.title}漫画分镜 ${i + 1}`} className="w-full object-cover" />
            </figure>
          ))}
          <div className="rounded-2xl border border-dashed border-primary-300 bg-primary-50/50 p-8 text-center">
            <i className="ri-sparkling-2-line text-3xl text-primary-500"></i>
            <p className="mt-3 font-medium text-foreground-900">本话到这里告一段落啦</p>
            <p className="mt-1 text-sm text-foreground-700">更多爆笑日常正在路上，明天记得回来继续追更～</p>
          </div>
        </div>
      </section>

      <section className="bg-background-100/70 py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-4 md:px-6">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">你可能会喜欢</h2>
            <Link to="/comics" className="text-sm text-primary-600 hover:text-primary-700 whitespace-nowrap">更多漫画 <i className="ri-arrow-right-line"></i></Link>
          </div>
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-3">
            {related.map((r) => (
              <Link
                key={r.id}
                to={`/comic/${r.id}`}
                className="group overflow-hidden rounded-2xl border border-background-200 bg-background-50 transition-all hover:-translate-y-1"
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <img src={r.cover} alt={`${r.title}封面`} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                </div>
                <div className="p-4">
                  <div className="text-xs text-primary-600">{r.categoryName}</div>
                  <h3 className="mt-1 truncate font-medium text-foreground-950">{r.title}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}